import {
  IconEllipsisHorizontal,
  Button,
  SequenceMenu,
  SequenceMenuDivider,
  GreyGrey70,
  RedRed60,
  Center
} from '@sequencehq/core-components'

import { useQuoteEditorKebabMenu } from './useQuoteEditorKebabMenu'
import { ConfirmDeleteLatestQuoteDraftModal } from '../modals/DeleteLatestQuoteDraftModal'
import { ArchiveQuoteModal } from 'common/modals/quotes/ArchiveQuote'
import { RevokeQuoteModal } from 'common/modals/quotes/RevokeQuote'
import { CreateTemplateFromQuoteMenuItem } from 'common/menuItems/quotes/CreateTemplateFromQuoteMenuItem'
import { SelectQuoteTemplateMenuItem } from 'common/menuItems/quotes/ApplyQuoteTemplate.menuItem'
import { AcceptQuoteMenuItem } from '../AcceptQuote'
import { CancelApprovalMenuItem } from 'common/menuItems/quotes/CancelApproval.menuItem'

export const QuoteEditorKebabMenu = () => {
  const {
    duplicateQuote,
    archiveQuote,
    discardChanges,
    saveAsTemplate,
    applyQuoteTemplate,
    modals,
    quoteId,
    revokeQuote,
    acceptQuote,
    cancelApproval
  } = useQuoteEditorKebabMenu()

  // If none of the actions are visible, don't show the menu as it would be empty
  if (
    !(
      duplicateQuote.visible ||
      archiveQuote.visible ||
      discardChanges.visible ||
      revokeQuote.visible ||
      cancelApproval.visible
    )
  ) {
    return null
  }

  return (
    <>
      <SequenceMenu
        width={226}
        data-testid="quote.menu"
        alignment="bottom-right"
        items={[
          ...(acceptQuote.visible
            ? [
                ({ setIsOpen }: { setIsOpen?: (open: boolean) => void }) => (
                  <AcceptQuoteMenuItem
                    key="accept-quote"
                    onSubmit={() => setIsOpen?.(false)}
                  />
                )
              ]
            : []),
          <CreateTemplateFromQuoteMenuItem
            key="create-template"
            onSuccess={saveAsTemplate.onSuccess}
            quoteId={quoteId}
            disabled={saveAsTemplate.disabled}
            disabledReason={saveAsTemplate.disabledReason}
          />,
          ...(applyQuoteTemplate.visible
            ? [
                ({
                  setIsOpen
                }: {
                  setIsOpen?: (newValue: boolean) => void
                }) => (
                  <SelectQuoteTemplateMenuItem
                    key="apply-template"
                    hasSalesforceOpportunityId={
                      applyQuoteTemplate.hasSalesforceOpportunityId
                    }
                    disabled={applyQuoteTemplate.disabled}
                    disabledReason={applyQuoteTemplate.disabledReason}
                    onSelect={quoteTemplate => {
                      applyQuoteTemplate.onSelection(quoteTemplate)
                      setIsOpen?.(false)
                    }}
                  />
                )
              ]
            : []),
          ...(duplicateQuote.visible
            ? [
                {
                  label: 'Duplicate quote',
                  onClick: duplicateQuote.onClick,
                  isDisabled: duplicateQuote.disabled,
                  tooltip: duplicateQuote.disabledReason,
                  'data-testid': 'quote.menu.duplicate',
                  divider:
                    revokeQuote.visible ||
                    cancelApproval.visible ||
                    archiveQuote.visible ||
                    discardChanges.visible
                }
              ]
            : []),
          ...(revokeQuote.visible
            ? [
                {
                  label: 'Remove quote access',
                  onClick: revokeQuote.onClick,
                  style: { color: RedRed60 },
                  'data-testid': 'quote.menu.revoke'
                }
              ]
            : []),
          ...(cancelApproval.visible
            ? [
                ({ setIsOpen }: { setIsOpen?: (open: boolean) => void }) => (
                  <CancelApprovalMenuItem
                    key="cancel-approval"
                    onSubmit={() => setIsOpen?.(false)}
                  />
                ),
                ...(archiveQuote.visible || discardChanges.visible
                  ? [<SequenceMenuDivider key="cancel-approval-divider" />]
                  : [])
              ]
            : []),
          ...(archiveQuote.visible
            ? [
                {
                  label: 'Archive quote',
                  onClick: archiveQuote.onClick,
                  style: { color: RedRed60 },
                  'data-testid': 'quote.menu.archive'
                }
              ]
            : []),
          ...(discardChanges.visible
            ? [
                {
                  label: 'Discard unpublished changes',
                  onClick: discardChanges.onClick,
                  style: { color: RedRed60 },
                  'data-testid': 'quote.menu.discardChanges'
                }
              ]
            : [])
        ]}
        menuButton={
          <Button variant="ghost" size="32px">
            <Center flexShrink="0">
              <IconEllipsisHorizontal color={GreyGrey70} />
            </Center>
          </Button>
        }
      />

      {modals.deleteLatestDraft.active && (
        <ConfirmDeleteLatestQuoteDraftModal
          submitting={modals.deleteLatestDraft.submitting}
          onClose={modals.deleteLatestDraft.onClose}
          onConfirm={modals.deleteLatestDraft.onConfirm}
        />
      )}

      {modals.archive.active && (
        <ArchiveQuoteModal
          isOpen
          quoteId={quoteId}
          onClose={modals.archive.onClose}
          onSuccess={modals.archive.onConfirm}
        />
      )}

      {modals.revoke.active && (
        <RevokeQuoteModal
          isOpen
          quoteId={quoteId}
          onClose={modals.revoke.onClose}
          onSuccess={modals.revoke.onConfirm}
        />
      )}
    </>
  )
}
